/**
 * useStudentLiveEntryState — l'avant-séance de l'élève : la séance, le test du matériel, le choix
 * du navigateur, le starting block, et l'entrée dans le live.
 *
 * Les règles d'avant (`PreCallScreen.js`), sur les mêmes sources — celles que le précall du masteur partage
 * passent par `useLiveEntryParts` :
 *  - la séance de la route, dans le compte ; arrivé par un lien direct, le compte est relu une fois,
 *    et au bout de 6 s sans séance on n'attend plus (l'entrée reste ouverte, sans test guidé) ;
 *  - le test du matériel (`useDeviceCheckMachine`), son souvenir (`precallMemory`), le périphérique
 *    choisi qui suit l'élève en séance (`setPrecallDevices`), sa vidéo (`videoCall.isVideoMuted`) ;
 *  - nouveau : son micro (`videoCall.isAudioMuted`), coupé ou ouvert avant d'entrer — le live le lit
 *    (`micGate`) et la salle n'ouvre pas à sa connexion un micro coupé ici. Le test, lui, vérifie le
 *    micro quel que soit ce choix : il dit si le matériel marche, pas comment l'élève entre ;
 *  - le test part seul dès qu'on sait quoi servir (la séance lue, ou 6 s sans elle), comme la salle
 *    d'attente d'une visio vérifie le matériel en arrivant : « Vérifier mon matériel » ne reste que
 *    s'il n'est pas parti ;
 *  - hors de Chrome sur ordinateur, le choix du navigateur passe d'abord ;
 *  - le starting block (`useStartingBlock`) se prépare pendant le test, quand la salle a le drapeau
 *    `checkpoint-sb` ; l'entrée attend sa préparation, Sam se pose à côté du bouton ;
 *  - entrer : le signalement silencieux à l'équipe si un voyant mesuré n'est pas au vert, puis le
 *    starting block s'il est prêt, sinon `setIsLiveModeEnabled(true, id)` et la relecture du compte —
 *    c'est ce qui ouvre le live, le legacy tant que le lot du live n'est pas là.
 *
 * Nouveau : chaque essai d'entrer dit comment il s'est fini (`precall.join`, `outcome`), y compris
 * quand l'élève repart sans entrer — on saura enfin si l'élève jamais entré a essayé.
 */
import { useNavigation } from "@react-navigation/native"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { apiRequest } from "../../../../../../data/redux/actions/api"
import { setIsLiveModeEnabled } from "../../../../../../data/redux/actions/liveMode"
import { forgetDeviceCheck } from "../../../../../../data/redux/actions/precallMemory"
import { setMuteAudio } from "../../../../../../data/redux/actions/videoCall"
import { authService } from "../../../../../../data/services/authService"
import { liveServiceV2 } from "../../../../../../data/services/liveServiceV2"
import { useStartingBlock } from "../../../../../../engine/precall/checkpoint/useStartingBlock"
import { type DeviceCheckView } from "../../../../../../engine/precall/deviceCheckMachine"
import { technicianAlert } from "../../../../../../engine/precall/liveEntry"
import { IS_WEB } from "../../../../../../engine/shared/platform"
import { type ApiSchoolSubject } from "../../../../adapters/parent/shared/schoolSubjectAdapter"
import { toLiveEntry, type ApiEntryAccount } from "../../../../adapters/student/LiveEntry/liveEntryAdapter"
import { entryTestShown } from "../../../../view-models/student/LiveEntry/deviceCheckViewModel"
import {
    liveEntryEquipment,
    liveEntryHeading,
    liveEntryJoin,
    liveEntryStatus,
} from "../../../../view-models/student/LiveEntry/liveEntryLobbyViewModel"
import { trackEntryJoin, trackTechnicianAlert } from "./liveEntryTracking"
import {
    useLiveEntryBrowser,
    useLiveEntryClock,
    useLiveEntryDeviceCheck,
    useLiveEntrySessionWait,
    useLiveEntryTracking,
    useLiveEntryVideoMuted,
} from "./useLiveEntryParts"
import { selectDisplayedStudent } from "../../../../view-models/shared/displayedStudent"

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ReduxState = any
type Navigation = { goBack: () => void }

export function useStudentLiveEntryState(liveSessionId: string) {
    const dispatch = useDispatch()
    const navigation = useNavigation() as unknown as Navigation

    const account = useSelector((state: ReduxState) => selectDisplayedStudent(state)) as ApiEntryAccount | undefined
    const subjects = useSelector((state: ReduxState) => state?.data?.staticData?.school_subjects) as
        ApiSchoolSubject[] | undefined
    const liveParticipants = useSelector((state: ReduxState) => state?.liveMode?.liveParticipants) as
        (number | string)[] | undefined
    // Le micro coupé avant d'entrer : le live le lit, et la salle ne l'ouvre pas à sa connexion (`micGate`).
    const isAudioMuted = useSelector((state: ReduxState) => state?.liveMode?.isAudioMuted === true)

    const entry = useMemo(
        () => toLiveEntry({ account, liveSessionId, apiSubjects: subjects, liveParticipants }),
        [account, liveSessionId, subjects, liveParticipants]
    )

    // Arrivé par un lien direct puis connecté : le compte n'a pas encore ses séances.
    const sessionTimedOut = useLiveEntrySessionWait(entry.found, account?.id)
    const tracking = useLiveEntryTracking(liveSessionId)

    const startingBlock = useStartingBlock({
        enabled: entry.startingBlockEnabled,
        liveSessionId,
        studentId: account?.id ?? null,
        role: "student",
    })
    const [showStartingBlock, setShowStartingBlock] = useState(false)

    // ── L'entrée ──
    const viewRef = useRef<DeviceCheckView | null>(null)
    const enteredRef = useRef(false)

    /** Ouvre le live (une fois les appareils rendus, au natif). */
    const openLive = useCallback(() => {
        if (enteredRef.current) return
        enteredRef.current = true
        if (entry.found && viewRef.current != null) {
            trackEntryJoin(tracking, viewRef.current, true, startingBlock.conversation != null)
        }
        dispatch(setIsLiveModeEnabled(true, liveSessionId))
        dispatch(apiRequest(authService.getUser) as never)
    }, [dispatch, entry.found, liveSessionId, startingBlock.conversation, tracking])

    // Le test, et l'entrée qui rend la caméra et le micro au natif avant d'ouvrir le live.
    const isVideoMuted = useLiveEntryVideoMuted()
    const { check, vm, releasing, enter } = useLiveEntryDeviceCheck({
        audience: "student",
        tracking,
        onEntered: openLive,
        // L'image seule, comme avant : le micro rendu attend sa marche sur appareil (suite du précall).
        releasesMicrophone: false,
        isVideoMuted,
    })
    viewRef.current = check.view

    // Le choix du navigateur, hors de Chrome sur ordinateur ; « Continuer sur ce navigateur » l'écarte.
    const { browser, dismissBrowser } = useLiveEntryBrowser()

    // Le test part seul, comme une salle d'attente de visio vérifie le matériel en arrivant : dès
    // qu'on sait quoi servir, hors du choix du navigateur. « Vérifier mon matériel » reste là s'il
    // n'est pas parti.
    const testShown = entryTestShown(entry.found, sessionTimedOut)
    const autoStartedRef = useRef(false)
    const { start } = check
    useEffect(() => {
        if (autoStartedRef.current || !testShown || browser != null || check.view.phase !== "intro") return
        autoStartedRef.current = true
        start()
    }, [testShown, browser, check.view.phase, start])

    const now = useLiveEntryClock()
    const lobby = useMemo(
        () => ({
            heading: liveEntryHeading(entry.session),
            status: liveEntryStatus(entry.session, entry.tutorHere, now),
            equipment: liveEntryEquipment(testShown ? vm : null),
            join: liveEntryJoin(testShown ? vm : null),
        }),
        [entry.session, entry.tutorHere, now, testShown, vm]
    )

    // L'image de la caméra n'est à l'écran que dans la salle d'attente, pas sous le starting block.
    const joinSession = useCallback(
        () => enter(!showStartingBlock && lobby.equipment.feedMounted),
        [enter, showStartingBlock, lobby.equipment.feedMounted]
    )

    const alertedRef = useRef(false)
    const onEnter = useCallback(() => {
        // L'équipe est prévenue en silence, une fois : l'élève n'attend personne.
        const alert = entry.found
            ? technicianAlert(check.view.report, { isVideoMuted, platform: tracking.platform })
            : null
        if (alert != null && !alertedRef.current) {
            alertedRef.current = true
            if (alert.forgetMemory) dispatch(forgetDeviceCheck())
            trackTechnicianAlert(tracking, alert.category)
            dispatch(apiRequest(liveServiceV2.submitIssueReport, { id: liveSessionId }, alert.payload) as never)
        }
        if (startingBlock.conversation != null) setShowStartingBlock(true)
        else joinSession()
    }, [
        check.view,
        dispatch,
        entry.found,
        isVideoMuted,
        joinSession,
        liveSessionId,
        startingBlock.conversation,
        tracking,
    ])

    // Reparti sans entrer : l'issue de l'essai, avec ce qui l'explique peut-être.
    useEffect(() => {
        const leave = () => {
            if (enteredRef.current || !entry.found || viewRef.current == null) return
            enteredRef.current = true
            trackEntryJoin(tracking, viewRef.current, false, false)
        }
        if (IS_WEB && typeof window !== "undefined") window.addEventListener("pagehide", leave)
        return () => {
            if (IS_WEB && typeof window !== "undefined") window.removeEventListener("pagehide", leave)
            leave()
        }
    }, [entry.found, tracking])

    return {
        entry,
        // Le test, même sans séance (au bout de 6 s) : l'ancien écran servait le sien.
        testShown,
        check,
        vm,
        lobby,
        releasing,
        browser,
        dismissBrowser,
        mic: { on: !isAudioMuted, toggle: () => dispatch(setMuteAudio(!isAudioMuted)) },
        entering: startingBlock.preparing || releasing,
        samWaiting: startingBlock.conversation != null && vm.entry === "ready",
        startingBlock: showStartingBlock ? startingBlock.conversation : null,
        onEnter,
        joinSession,
        onBack: () => navigation.goBack(),
    }
}
